import React from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { QUOTA_VALIDATION } from '../constants';

const useStyles = makeStyles((theme) => ({
  quotaRow: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  },
}));

// Format number with commas and round for display
const formatNumberWithCommas = (value) => {
  if (!value) return '';
  // Round the value first before formatting
  const roundedValue = Math.round(Number(value));
  const numericValue = String(roundedValue).replace(/[^\d]/g, '');
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const QuotaAssignmentRow = ({
  assignment,
  onQuotaChange,
  onRemove,
  allAvailableQuarters,
  isSaving,
  currencySymbol,
  hideRampStatus = false,
  assignmentsLength = 1,
}) => {
  const classes = useStyles();

  const handleInputChange = (field, value) => {
    const numericValue = value.replace(/,/g, '');

    // Allow empty value
    if (numericValue === '' || numericValue === '-') {
      onQuotaChange(assignment.id, field, numericValue);
      return;
    }

    // Validate the numeric value is within 32-bit signed integer range
    const parsedValue = parseInt(numericValue, 10);

    // Check if it's a valid number and within the defined quota validation range
    if (!isNaN(parsedValue) && parsedValue >= QUOTA_VALIDATION.MIN_VALUE && parsedValue <= QUOTA_VALIDATION.MAX_VALUE) {
      onQuotaChange(assignment.id, field, numericValue);
    }
    // If the value is out of range or invalid, don't update the field (silently ignore)
  };

  return (
    <Box className={classes.quotaRow}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={hideRampStatus ? 2 : 2}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Fiscal Quarter</InputLabel>
            <Select
              value={assignment.fiscalQuarter}
              onChange={(e) => onQuotaChange(assignment.id, 'fiscalQuarter', e.target.value)}
              label="Fiscal Quarter"
              disabled={isSaving}
            >
              {[...allAvailableQuarters].reverse().map((quarter) => (
                <MenuItem key={quarter} value={quarter}>
                  {quarter}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={hideRampStatus ? 5 : 4}>
          <TextField
            fullWidth
            variant="outlined"
            label="Quota"
            placeholder="Enter amount"
            value={formatNumberWithCommas(assignment.quota)}
            onChange={(e) => handleInputChange('quota', e.target.value)}
            disabled={isSaving}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{currencySymbol}</InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={hideRampStatus ? 4 : 3}>
          <TextField
            fullWidth
            variant="outlined"
            label="NQP Goal"
            placeholder="Enter amount"
            value={formatNumberWithCommas(assignment.nqpGoal)}
            onChange={(e) => handleInputChange('nqpGoal', e.target.value)}
            disabled={isSaving}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{currencySymbol}</InputAdornment>
              ),
            }}
          />
        </Grid>
        {!hideRampStatus && (
          <Grid item xs={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Ramp Status</InputLabel>
              <Select
                value={assignment.rampStatus}
                onChange={(e) => onQuotaChange(assignment.id, 'rampStatus', e.target.value)}
                label="Ramp Status"
                disabled={isSaving}
              >
                <MenuItem value="Ramping">Ramping</MenuItem>
                <MenuItem value="Ramped">Ramped</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item container justifyContent='center' xs={1}>
          {assignmentsLength > 1 && (
            <IconButton
              size="small"
              onClick={() => onRemove(assignment.id)}
              disabled={isSaving}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default QuotaAssignmentRow;
